import { IAgentConversation, IWorkflowTemplateVersion } from '@commenda/types';
import { useEffect, useRef } from 'react';
import { TBuilderCanvasBinding } from './types/types';

/** Keeps the canvas and the open thread in step with what the server reports for this version. */
export function useBuilderCanvasSync(
  params: Readonly<{
    canvas: TBuilderCanvasBinding;
    polledVersion: IWorkflowTemplateVersion | null;
    conversationId: string | null;
    conversations: IAgentConversation[];
    isEditInProgress: boolean;
    isEditable: boolean;
    onSelectConversation: (conversationId: string) => void;
  }>
) {
  const {
    canvas,
    polledVersion,
    conversationId,
    conversations,
    isEditInProgress,
    isEditable,
    onSelectConversation,
  } = params;

  // Open the workflow's most recent chat once, so the panel does not start empty on a workflow that
  // already has a history.
  const hasAdoptedRecentRef = useRef(false);
  useEffect(() => {
    if (conversationId || hasAdoptedRecentRef.current) return;
    const mostRecent = conversations[0];
    if (!mostRecent) return;
    hasAdoptedRecentRef.current = true;
    onSelectConversation(mostRecent.id);
  }, [conversationId, conversations, onSelectConversation]);

  // An effect because this writes to the editor's store, which cannot happen during render.
  useEffect(() => {
    canvas.setIsReadOnly(isEditInProgress || !isEditable);
  }, [isEditInProgress, isEditable, canvas.setIsReadOnly]);

  // Apply only what the server sent, and only when it differs, so an unchanged poll does not churn
  // undo history.
  const lastAppliedJsonRef = useRef<string | null>(null);
  useEffect(() => {
    const remote = polledVersion?.definition;
    if (!remote) return;
    const serialized = JSON.stringify(remote);
    if (serialized === lastAppliedJsonRef.current) return;
    lastAppliedJsonRef.current = serialized;
    canvas.applyRemoteWorkflow(remote);
  }, [polledVersion, canvas.applyRemoteWorkflow]);
}
